'use client';

import { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import commonStyles from '@/app/(website)/commonStyles.module.scss';
import styles from './testimonials.module.scss';
import { Button, ArrowRightLong } from '@baseline/ui';
import { Data } from '@strapi/strapi';
import { useAutoRotate } from './hooks/useAutoRotate';

export type Testimonial = {
  id?: string | number;
  quote: string;
  name: string;
  title?: string;
};

type TestimonialsProps =
  | Data.ContentType<'api::section-testimonial.section-testimonial'>
  | Data.Component<'shared.testimonial'>;

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 40 : -40,
    opacity: 0
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction > 0 ? -40 : 40,
    opacity: 0
  })
};

export default function Testimonials(props: Readonly<TestimonialsProps>) {
  const {
    header = 'Trusted by leading private lenders across America',
    testimonial_entry = []
  } = props;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const length = testimonial_entry?.length || 0;

  const paginate = useCallback(
    (dir: number) => {
      setDirection(dir);
      setIndex((prev) => (prev + dir + length) % length);
    },
    [length]
  );

  const handleNextPagination = useCallback(() => paginate(1), [paginate]);

  const { containerProps } = useAutoRotate({
    length,
    interval: 5000,
    onNext: handleNextPagination
  });

  const active = testimonial_entry?.[index];

  return (
    <section
      className={`${commonStyles.sectionContent} ${styles.testimonials}`}
      aria-label="Testimonials"
    >
      <h2 className={commonStyles.sectionSubheadingH2}>{header}</h2>
      <div className={styles.divider} aria-hidden="true" />
      <div className={styles.carousel} {...containerProps}>
        <div className={styles.viewport}>
          <AnimatePresence initial={false} custom={direction} mode="popLayout">
            <motion.figure
              key={active?.id ?? index}
              className={styles.slide}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: 'easeOut' }}
            >
              <blockquote
                className={`${styles.quote} ${commonStyles.blockQuoteText}`}
              >
                {active?.quote}
              </blockquote>
            </motion.figure>
          </AnimatePresence>
        </div>
        {length > 1 ? (
          <div className={styles.controls} aria-label="Carousel navigation">
            <Button
              size="icon"
              className={styles.ctaButton}
              colorScheme="tertiary"
              aria-label="Previous testimonial"
              onClick={() => paginate(-1)}
            >
              <ArrowRightLong
                style={{
                  width: '1.25rem',
                  height: '1.25rem',
                  transform: 'rotate(180deg)'
                }}
              />
            </Button>
            <div className={styles.attribution} aria-live="polite">
              <span className={styles.name}>{active?.name}</span>
              {active?.title && (
                <span className={styles.title}>{active?.title}</span>
              )}
            </div>
            <Button
              size="icon"
              className={styles.ctaButton}
              colorScheme="tertiary"
              aria-label="Next testimonial"
              onClick={() => paginate(1)}
            >
              <ArrowRightLong style={{ width: '1.25rem', height: '1.25rem' }} />
            </Button>
          </div>
        ) : (
          <div className={styles.attribution} aria-live="polite">
            <span className={styles.name}>{active?.name}</span>
            {active?.title && (
              <span className={styles.title}>{active?.title}</span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
